/**
 * Uma vaga sem foto: ensina o que fotografar e abre o seletor de arquivo (só PNG e JPEG). A mesma vaga mostra
 * o envio em andamento e o erro do envio, com a mensagem do servidor.
 */
import React from 'react';
import { Box, Button, CircularProgress, Typography } from '@mui/material';

export type UploadAttempt = { status: 'sending'; fileName: string } | { status: 'error'; message: string };

interface EmptyPhotoSlotProps {
  label: string;
  attempt: UploadAttempt | null;
  /** Outra foto está sendo enviada: uma por vez. */
  busy: boolean;
  disabled: boolean;
  onFile: (file: File) => void;
}

export const EmptyPhotoSlot: React.FC<EmptyPhotoSlotProps> = ({ label, attempt, busy, disabled, onFile }) => {
  const sending = attempt?.status === 'sending';
  return (
    <Box
      component="li"
      sx={{
        listStyle: 'none',
        border: '2px dashed',
        borderColor: 'grey.500',
        borderRadius: 1,
        p: 2,
        minHeight: 160,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        justifyContent: 'center',
        gap: 1,
      }}
    >
      {sending ? (
        <Box role="status" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CircularProgress size={20} />
          <Typography variant="body1">{`Enviando ${attempt.fileName}…`}</Typography>
        </Box>
      ) : (
        <>
          <Typography variant="body1" component="b" sx={{ fontWeight: 700 }}>
            {label}
          </Typography>
          <Typography variant="body1">Fotografe o posto, a postura e a bancada.</Typography>
          {attempt?.status === 'error' && (
            <Typography role="alert" variant="body1" sx={{ color: 'error.dark' }}>
              {`Erro: ${attempt.message}`}
            </Typography>
          )}
          <Button
            component="label"
            variant="outlined"
            disabled={disabled || busy}
            sx={{ minHeight: 44 }}
          >
            {attempt?.status === 'error' ? 'Escolher outra foto' : 'Escolher foto'}
            <input
              hidden
              type="file"
              accept="image/png,image/jpeg"
              onChange={(event) => {
                const file = event.target.files?.[0];
                event.target.value = '';
                if (file) onFile(file);
              }}
            />
          </Button>
        </>
      )}
    </Box>
  );
};
